
import React from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Bell, X, MapPin, Calendar, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/auth/AuthProvider";

interface UpdateItem {
  id: string;
  type: "response" | "message" | "opening" | "reminder";
  title: string;
  description: string;
  date: string;
  read: boolean;
  link?: string;
}

// Mock data - in a real app, this would come from an API or state
const mockUpdates: UpdateItem[] = [
  {
    id: "1",
    type: "response",
    title: "Response from St. Mary's",
    description: "Your application has been received and is being reviewed.",
    date: "2h ago",
    read: false,
    link: "/dashboard/leads/1"
  },
  {
    id: "2",
    type: "opening",
    title: "New opening in Dublin South",
    description: "A new vacancy has become available at a home you're interested in.",
    date: "Yesterday",
    read: true,
    link: "/nursing-homes/dublin/st-marys"
  },
  {
    id: "3",
    type: "reminder",
    title: "Complete your profile",
    description: "Add your care preferences to get matched with suitable homes.",
    date: "3 days ago",
    read: true,
    link: "/dashboard?tab=profile"
  }
];

export default function MobileUpdatesDrawer() {
  const { user } = useAuth();
  
  // If no user, don't show any notifications
  if (!user) {
    return null;
  }
  
  const unreadCount = mockUpdates.filter(update => !update.read).length;
  
  return (
    <Drawer direction="right">
      <DrawerTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="h-[85vh]">
        <DrawerHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-xl font-bold text-grandpal-green">Updates</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DrawerClose>
          </div>
          <DrawerDescription>
            Recent updates on your nursing home journey
          </DrawerDescription>
        </DrawerHeader>
        
        <div className="flex-1 overflow-y-auto px-4">
          {mockUpdates.length > 0 ? (
            <div className="divide-y">
              {mockUpdates.map((update) => (
                <div 
                  key={update.id} 
                  className={`py-4 ${!update.read ? 'bg-grandpal-beige/30' : ''}`}
                >
                  <div className="flex gap-3">
                    <div className="bg-grandpal-green/10 rounded-full p-2 h-10 w-10 flex items-center justify-center flex-shrink-0">
                      {update.type === "response" && <MessageSquare className="h-5 w-5 text-grandpal-green" />}
                      {update.type === "opening" && <MapPin className="h-5 w-5 text-grandpal-green" />}
                      {update.type === "reminder" && <Calendar className="h-5 w-5 text-grandpal-green" />}
                      {update.type === "message" && <MessageSquare className="h-5 w-5 text-grandpal-green" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{update.title}</h4>
                      <p className="text-sm text-gray-600 my-1">{update.description}</p>
                      <div className="flex justify-between mt-2">
                        <span className="text-xs text-gray-500">{update.date}</span>
                        {update.link && (
                          <a 
                            href={update.link} 
                            className="text-xs text-grandpal-green font-medium flex items-center"
                          >
                            View details
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40">
              <p className="text-gray-500">No new updates</p>
            </div>
          )}
        </div>
        
        <DrawerFooter className="border-t pt-4">
          <Button 
            variant="outline" 
            className="w-full border-grandpal-green text-grandpal-green hover:bg-grandpal-green hover:text-white"
          >
            Mark all as read
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
